import { Link } from 'react-router'
import { Button } from '@/components/Button'
import { Checkbox } from '@/components/Checkbox'
import { Dropdown, DropdownItem, DropdownTitle } from '@/components/Dropdown'
import { Icon } from '@/components/Icon'
import { IconButton } from '@/components/IconButton'
import { Pill } from '@/components/Pill'
import { DateCell, ImageCell, TableCell, TextCell } from '@/components/Table'
import { links } from '@/utils/link'
import { Id } from '@/infra/id'
import { pluralize } from '@/utils/pluralization-util'
import { type ColumnId } from '@/features/documents/document-table-columns'
import styles from './DocumentManagementTableRow.module.css'

export interface DocumentManagementTableLinkedJob {
  id: Id
  displayName: string
}

export interface DocumentManagementTableRowProps {
  columnIds: ColumnId[]
  documentId: Id
  title: string
  archived: boolean
  workspaceName: string
  workspaceIcon: string
  fileType: string
  dateModified: Date
  linkedJobs: DocumentManagementTableLinkedJob[]
  isSelected: boolean
  onToggleSelected: (checked: boolean) => void
  onDownload: () => void
  onShowDetails: () => void
  onDelete: () => void
}

export function DocumentManagementTableRow({
  columnIds,
  documentId,
  title,
  archived,
  workspaceName,
  workspaceIcon,
  fileType,
  dateModified,
  linkedJobs,
  isSelected,
  onToggleSelected,
  onDownload,
  onShowDetails,
  onDelete,
}: DocumentManagementTableRowProps) {
  return columnIds.map((columnId) => {
    switch (columnId) {
      case 'Checkbox':
        return (
          <TableCell key={columnId}>
            <div>
              <Checkbox
                muteClickEvent
                checked={isSelected}
                onChange={onToggleSelected}
                testId={`document-management-table-row-checkbox-${documentId}`}
              />
            </div>
          </TableCell>
        )
      case 'DocumentTitle':
        return (
          <TextCell
            key={columnId}
            testId={`document-management-table-row-name-${documentId}`}
          >
            <div className={styles.titleContainer}>
              <span>{title}</span>
              {archived && (
                <span className={styles.pillContainer}>
                  <Pill color="red" shape="square" trim>
                    Archived
                  </Pill>
                </span>
              )}
            </div>
          </TextCell>
        )
      case 'Workspace':
        return (
          <ImageCell
            title={workspaceName}
            image={workspaceIcon}
            testId={`document-management-table-row-workspace-${documentId}`}
            key={columnId}
          />
        )
      case 'Type':
        return (
          <TextCell
            key={columnId}
            testId={`document-management-table-row-type-${documentId}`}
          >
            {fileType !== 'file'
              ? fileType.toUpperCase()
              : (/\.\w+$/.exec(title)?.join().replace('.', '').toUpperCase() ??
                'Document')}
          </TextCell>
        )
      case 'DateModified':
        return (
          <DateCell
            date={dateModified}
            testId={`document-management-table-row-date-modified-${documentId}`}
            key={columnId}
          />
        )
      case 'LinkedJob':
        return (
          <TableCell key={columnId}>
            <Dropdown
              autoDismiss
              position="bottom-end"
              isDisabled={linkedJobs.length < 1}
              testId={`document-management-table-row-linked-job-dropdown-${documentId}`}
              button={
                <Button
                  label={`${linkedJobs.length} linked ${pluralize('job', linkedJobs.length)}`}
                  variant="secondary"
                />
              }
            >
              {linkedJobs.map((job) => (
                <DropdownItem
                  key={job.id}
                  icon={<Icon name="link" color="var(--grey600)" />}
                  muteClickEvent
                >
                  <Link
                    className={styles.link}
                    to={links.job(Id.assert(job.id))}
                    data-test={`document-management-table-row-linked-job-dropdown-link-${job.id}`}
                  >
                    <div className={styles.linkInfoContainer}>
                      {job.displayName && (
                        <span className={styles.linkInfoTitle}>
                          {job.displayName}
                        </span>
                      )}
                      <span className={styles.linkInfoSubtitle}>{job.id}</span>
                    </div>
                  </Link>
                </DropdownItem>
              ))}
            </Dropdown>
          </TableCell>
        )
      case 'Action':
        return (
          <TableCell key={columnId}>
            <Dropdown
              autoDismiss
              position="bottom-end"
              testId={`document-management-table-row-actions-${documentId}`}
              button={
                <IconButton
                  name="more"
                  label="Document actions"
                  color="var(--grey600)"
                />
              }
            >
              <DropdownTitle>Actions</DropdownTitle>
              <DropdownItem
                onPress={onDownload}
                icon={<Icon name="download" color="var(--grey600)" />}
                buttonTestId="document-management-table-row-actions-download"
                muteClickEvent
              >
                Download
              </DropdownItem>
              <DropdownItem
                onPress={onShowDetails}
                icon={<Icon name="edit-outlined" color="var(--grey600)" />}
                buttonTestId="document-management-table-row-actions-details"
                muteClickEvent
              >
                Details
              </DropdownItem>
              {!archived && (
                <DropdownItem
                  onPress={onDelete}
                  icon={<Icon name="delete" color="var(--grey600)" />}
                  buttonTestId="document-management-table-row-actions-delete"
                  muteClickEvent
                >
                  Delete
                </DropdownItem>
              )}
            </Dropdown>
          </TableCell>
        )
    }
  })
}
